/**
 * Talker may edit an upcoming 15-minute talk session (format + slot)
 * before start. After leave, session start, cancel, or complete: no edit.
 */

import { isTalkerLateCancel } from './cancelPolicy';
import { bookingIsInSession, bookingTimes } from './sessionClock';
import { transactionWasLeft } from './sessionLeave';
import {
  SESSION_FORMATS,
  SESSION_LENGTH_MINUTES,
  isTalkSessionTransaction,
  priceInSubunitsForFormat,
  resolveSessionFormat,
} from './sessionPricing';

export const EDIT_BOOKING_CODES = {
  NOT_CUSTOMER: 'not_customer',
  NOT_TALK_SESSION: 'not_talk_session',
  NOT_UPCOMING: 'not_upcoming',
  SESSION_LEFT: 'session_left',
  IN_SESSION: 'in_session',
  CANCELED: 'canceled',
  FORMAT_INVALID: 'format_invalid',
  FORMAT_NOT_OFFERED: 'format_not_offered',
  WINDOW_INVALID: 'window_invalid',
  SEAT_UNAVAILABLE: 'seat_unavailable',
  RATE_LIMITED: 'rate_limited',
  CANCEL_FAILED: 'cancel_failed',
  NO_PAYMENT_METHOD: 'no_payment_method',
  HOLD_FAILED: 'hold_failed',
  UPDATE_FAILED: 'update_failed',
};

/** Talker Edit booking opens `/order/:id?edit=1` so the modal cannot miss on iOS tap-through. */
export const EDIT_BOOKING_QUERY = 'edit';

const partyId = entity => {
  if (!entity) {
    return null;
  }
  if (typeof entity === 'string') {
    return entity;
  }
  return entity.uuid || entity.id?.uuid || (typeof entity.id === 'string' ? entity.id : null);
};

/**
 * Booking role for the signed-in user, from customer/provider ids — not the
 * inbox tab. Dual-role accounts (talker + listener) must use this so Edit
 * never appears on a booking they host as listener.
 *
 * @param {Object} [tx]
 * @param {Object} [currentUser]
 * @returns {'customer'|'provider'|null}
 */
export const bookingPartyRole = (tx, currentUser) => {
  const userId = partyId(currentUser) || partyId(currentUser?.id);
  if (!userId) {
    return null;
  }
  const customerId = partyId(tx?.customer);
  const providerId = partyId(tx?.provider);
  if (customerId && customerId === userId) {
    return 'customer';
  }
  if (providerId && providerId === userId) {
    return 'provider';
  }
  return null;
};

/**
 * Inbox / tx UI role: party on this booking, else the tab fallback.
 *
 * @param {Object} [tx]
 * @param {Object} [currentUser]
 * @param {string} [fallbackRole]
 * @returns {string|null}
 */
export const inboxRoleForBooking = (tx, currentUser, fallbackRole) => {
  return bookingPartyRole(tx, currentUser) || fallbackRole || null;
};

/**
 * Keep a My Bookings row on this tab only when the user is that party.
 * Unknown / incomplete entities stay visible so fixtures still render.
 *
 * @param {Object} [tx]
 * @param {Object} [currentUser]
 * @param {'customer'|'provider'} tabRole
 * @returns {boolean}
 */
export const matchesInboxTabRole = (tx, currentUser, tabRole) => {
  const party = bookingPartyRole(tx, currentUser);
  if (!party) {
    return true;
  }
  return party === tabRole;
};

/**
 * True when the order URL asked to open the talker edit flow.
 *
 * @param {string|Object} [search]
 * @returns {boolean}
 */
export const isEditBookingQuery = search => {
  if (search == null || search === '') {
    return false;
  }
  if (typeof search === 'object') {
    const value = search[EDIT_BOOKING_QUERY];
    return value === '1' || value === 1 || value === true;
  }
  return /(?:^|[?&])edit=1(?:&|$)/.test(String(search));
};

export const editBookingSearch = () => `?${EDIT_BOOKING_QUERY}=1`;

const EDIT_BOOKING_INTENT_STORAGE_KEY = 'sl.editBookingIntent';

/**
 * Remember that the talker tapped Edit booking. iOS Safari can remount
 * TransactionPage and drop `?edit=1`; sessionStorage survives that.
 *
 * @param {string} [transactionId]
 */
export const rememberTalkerEditBookingIntent = transactionId => {
  if (typeof window === 'undefined' || !transactionId) {
    return;
  }
  try {
    window.sessionStorage.setItem(EDIT_BOOKING_INTENT_STORAGE_KEY, String(transactionId));
  } catch (e) {
    // Private mode / quota must not block navigation.
  }
};

/**
 * True when this tab still owes the talker the edit modal for `transactionId`.
 *
 * @param {string} [transactionId]
 * @returns {boolean}
 */
export const peekTalkerEditBookingIntent = transactionId => {
  if (typeof window === 'undefined' || !transactionId) {
    return false;
  }
  try {
    return window.sessionStorage.getItem(EDIT_BOOKING_INTENT_STORAGE_KEY) === String(transactionId);
  } catch (e) {
    return false;
  }
};

/**
 * Clear the stored edit-booking intent after the modal closes.
 *
 * @param {string} [transactionId]
 */
export const clearTalkerEditBookingIntent = transactionId => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const stored = window.sessionStorage.getItem(EDIT_BOOKING_INTENT_STORAGE_KEY);
    if (!transactionId || stored === String(transactionId)) {
      window.sessionStorage.removeItem(EDIT_BOOKING_INTENT_STORAGE_KEY);
    }
  } catch (e) {
    // ignore
  }
};

/**
 * Open the editor from `?edit=1`, the live URL (Safari Router miss), or storage.
 *
 * @param {Object} [opts]
 * @param {string|Object} [opts.search]
 * @param {string} [opts.transactionId]
 * @returns {boolean}
 */
export const hasTalkerEditBookingIntent = ({ search, transactionId } = {}) => {
  if (isEditBookingQuery(search)) {
    return true;
  }
  if (typeof window !== 'undefined' && isEditBookingQuery(window.location?.search)) {
    return true;
  }
  return peekTalkerEditBookingIntent(transactionId);
};

const CANCELED_TRANSITIONS = new Set([
  'transition/cancel',
  'transition/customer-cancel',
  'transition/customer-cancel-request',
  'transition/provider-cancel',
  'transition/decline',
  'transition/operator-decline',
  'transition/expire',
  'transition/expire-payment',
  'transition/expire-accepted',
  'transition/operator-cancel-separate-hold',
]);

const COMPLETED_TRANSITIONS = new Set([
  'transition/charge-after-session',
  'transition/provider-charge-after-session',
  'transition/complete',
  'transition/operator-complete',
  'transition/operator-complete-separate-hold',
  'transition/review-1-by-customer',
  'transition/review-1-by-provider',
  'transition/review-2-by-customer',
  'transition/review-2-by-provider',
  'transition/expire-review-period',
  'transition/expire-customer-review-period',
  'transition/expire-provider-review-period',
]);

const PAID_UPCOMING_TRANSITIONS = new Set([
  'transition/accept',
  'transition/operator-accept',
]);

const REQUESTED_UPCOMING_TRANSITIONS = new Set([
  'transition/confirm-payment',
  'transition/request-payment',
  'transition/remind-provider-accept',
]);

const ARCHIVE_OK_STATES = new Set([
  'canceled',
  'cancelled',
  'declined',
  'expired',
  'payment-expired',
  'delivered',
  'reviewed',
  'reviewed-by-customer',
  'reviewed-by-provider',
  'past',
  'no-confirmation',
]);

const SESSION_LENGTH_MS = SESSION_LENGTH_MINUTES * 60 * 1000;

/** Talker pending cancel (`customer-cancel-request`) exists on process v8+. */
export const CUSTOMER_CANCEL_REQUEST_MIN_PROCESS_VERSION = 8;

const asDate = value => {
  if (!value && value !== 0) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }
  const ms = typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(ms) ? new Date(ms) : null;
};

const lastTransitionOf = tx => tx?.attributes?.lastTransition || null;

const flagId = value => {
  if (!value && value !== 0) {
    return null;
  }
  if (typeof value === 'string' && value) {
    return value;
  }
  if (typeof value.uuid === 'string' && value.uuid) {
    return value.uuid;
  }
  return null;
};

/**
 * True when this row was replaced by an edit. The original booking start
 * must not drive no-show, banner, reminder, or expire clocks.
 *
 * @param {Object} [tx]
 * @returns {boolean}
 */
export const isSupersededEditedBooking = tx => {
  const flags = {
    ...(tx?.attributes?.protectedData || {}),
    ...(tx?.attributes?.metadata || {}),
  };
  if (flags.replacedByEdit === true || flags.editRollback === true) {
    return true;
  }
  return !!flagId(flags.replacedByTransactionId);
};

const processVersionOf = tx => {
  const version = Number(tx?.attributes?.processVersion);
  return Number.isFinite(version) ? version : null;
};

const processStateOf = tx => {
  const process = tx?.attributes?.processName;
  if (!process) {
    return null;
  }
  try {
    // Lazy require would cycle on the server; callers may pass processState.
    return tx?.attributes?.processState || null;
  } catch (e) {
    return null;
  }
};

/**
 * True when this talker booking can still be edited.
 *
 * @param {Object} [tx]
 * @param {Object} [opts]
 * @param {string} [opts.role]
 * @param {Date|number} [opts.now]
 * @param {string} [opts.processState]
 * @returns {boolean}
 */
export const canEditUpcomingTalkBooking = (tx, { role, now = new Date(), processState } = {}) => {
  return !editUpcomingBookingBlockReason(tx, { role, now, processState });
};

const isRequestedUpcomingState = (state, last) =>
  state === 'preauthorized' || REQUESTED_UPCOMING_TRANSITIONS.has(last);

const isAcceptedUpcomingState = (state, last) =>
  state === 'accepted' || PAID_UPCOMING_TRANSITIONS.has(last);

/**
 * True when this upcoming talk session can still be canceled (talker or
 * listener). Requested (preauthorized) and accepted both qualify, until
 * start or until both people have joined.
 *
 * @param {Object} [tx]
 * @param {Object} [opts]
 * @param {Date|number} [opts.now]
 * @param {string} [opts.processState]
 * @param {boolean} [opts.bothJoined]
 * @returns {boolean}
 */
export const canCancelUpcomingTalkBooking = (
  tx,
  { now = new Date(), processState, bothJoined = false } = {}
) => {
  if (!isTalkSessionTransaction(tx)) {
    return false;
  }
  if (transactionWasLeft(tx)) {
    return false;
  }
  if (isSupersededEditedBooking(tx)) {
    return false;
  }
  if (bothJoined) {
    return false;
  }
  const last = lastTransitionOf(tx);
  if (CANCELED_TRANSITIONS.has(last) || COMPLETED_TRANSITIONS.has(last)) {
    return false;
  }
  const state = processState || processStateOf(tx);
  if (state === 'canceled' || state === 'cancelled' || state === 'declined' || state === 'expired') {
    return false;
  }
  if (
    state === 'delivered' ||
    state === 'reviewed' ||
    state === 'reviewed-by-customer' ||
    state === 'reviewed-by-provider'
  ) {
    return false;
  }
  const upcoming = isAcceptedUpcomingState(state, last) || isRequestedUpcomingState(state, last);
  if (state && !upcoming) {
    return false;
  }
  if (!state && !upcoming) {
    return false;
  }
  const nowDate = now instanceof Date ? now : new Date(now);
  if (bookingIsInSession(tx, nowDate)) {
    return false;
  }
  const { start, end } = bookingTimes(tx);
  const nowMs = nowDate.getTime();
  if (end && nowMs >= end.getTime()) {
    return false;
  }
  if (start && nowMs >= start.getTime()) {
    return false;
  }
  return true;
};

/**
 * True when this talker pending request is on a process version that
 * does not have :transition/customer-cancel-request (v7 and earlier).
 * Missing processVersion is treated as unknown — try the transition
 * and catch HTTP 400.
 *
 * @param {Object} [tx]
 * @param {Object} [opts]
 * @param {string} [opts.processState]
 * @returns {boolean}
 */
export const isLegacyTalkerPendingCancel = (tx, { processState } = {}) => {
  const last = lastTransitionOf(tx);
  const state = processState || processStateOf(tx);
  const requested = isRequestedUpcomingState(state, last) && !isAcceptedUpcomingState(state, last);
  if (!requested) {
    return false;
  }
  const version = processVersionOf(tx);
  return version != null && version < CUSTOMER_CANCEL_REQUEST_MIN_PROCESS_VERSION;
};

/**
 * Sharetribe 400 (or rejected thunk payload) when customer-cancel-request
 * is not on this transaction's process version.
 *
 * @param {Object} [error]
 * @returns {boolean}
 */
export const isTalkerPendingCancelUnavailableError = error => {
  if (!error) {
    return false;
  }
  const status = error.status || error.statusCode || error.payload?.status;
  if (status === 400) {
    return true;
  }
  const text = [
    error.message,
    error.statusText,
    error.payload?.message,
    error.payload?.statusText,
    error.error?.message,
  ]
    .filter(Boolean)
    .join(' ');
  return /customer-cancel-request|unknown transition|transition is not available|invalid transition/i.test(
    text
  );
};

/**
 * Process transition that cancels this upcoming talk session for `role`.
 * Talker requested uses customer-cancel-request on process v8+.
 * Listener requested uses decline. Accepted listener uses provider-cancel.
 * Accepted talker: customer-cancel when free (≥ cutoff / Start now);
 * charge-after-session when inside the 1-hour window (same as a no-show).
 *
 * @param {Object} [tx]
 * @param {Object} [opts]
 * @param {string} [opts.role]
 * @param {string} [opts.processState]
 * @param {Date|number} [opts.now]
 * @returns {string|null}
 */
export const cancelTransitionForUpcomingTalkBooking = (
  tx,
  { role, processState, now } = {}
) => {
  if (!canCancelUpcomingTalkBooking(tx, { processState, now })) {
    return null;
  }
  const last = lastTransitionOf(tx);
  const state = processState || processStateOf(tx);
  const requested = isRequestedUpcomingState(state, last) && !isAcceptedUpcomingState(state, last);
  const isProvider = role === 'provider';
  if (isProvider) {
    return requested ? 'transition/decline' : 'transition/provider-cancel';
  }
  if (requested) {
    return isLegacyTalkerPendingCancel(tx, { processState })
      ? null
      : 'transition/customer-cancel-request';
  }
  if (isTalkerLateCancel(tx, { now, role })) {
    return 'transition/charge-after-session';
  }
  return 'transition/customer-cancel';
};

/**
 * Inbox Delete hides a conversation. Only past / cancelled / completed
 * talk rows may offer it — never upcoming or requested sessions.
 *
 * @param {Object} [tx]
 * @param {Object} [opts]
 * @param {string} [opts.processState]
 * @param {Date|number} [opts.now]
 * @returns {boolean}
 */
export const canDeleteInboxTalkConversation = (tx, { processState, now = new Date() } = {}) => {
  if (!isTalkSessionTransaction(tx)) {
    return true;
  }
  const last = lastTransitionOf(tx);
  if (CANCELED_TRANSITIONS.has(last) || COMPLETED_TRANSITIONS.has(last)) {
    return true;
  }
  const state = processState || processStateOf(tx);
  if (ARCHIVE_OK_STATES.has(state)) {
    return true;
  }
  if (transactionWasLeft(tx)) {
    return true;
  }
  const nowDate = now instanceof Date ? now : new Date(now);
  const { end } = bookingTimes(tx);
  if (end && nowDate.getTime() >= end.getTime() && !isAcceptedUpcomingState(state, last)) {
    return true;
  }
  return false;
};

/**
 * Machine reason the talker cannot edit, or null when edit is allowed.
 *
 * @param {Object} [tx]
 * @param {Object} [opts]
 * @returns {string|null}
 */
export const editUpcomingBookingBlockReason = (
  tx,
  { role, now = new Date(), processState } = {}
) => {
  if (role && role !== 'customer') {
    return EDIT_BOOKING_CODES.NOT_CUSTOMER;
  }
  if (!isTalkSessionTransaction(tx)) {
    return EDIT_BOOKING_CODES.NOT_TALK_SESSION;
  }
  if (transactionWasLeft(tx)) {
    return EDIT_BOOKING_CODES.SESSION_LEFT;
  }
  if (isSupersededEditedBooking(tx)) {
    return EDIT_BOOKING_CODES.CANCELED;
  }
  const last = lastTransitionOf(tx);
  if (CANCELED_TRANSITIONS.has(last)) {
    return EDIT_BOOKING_CODES.CANCELED;
  }
  if (COMPLETED_TRANSITIONS.has(last)) {
    return EDIT_BOOKING_CODES.NOT_UPCOMING;
  }
  const state = processState || processStateOf(tx);
  if (state === 'canceled' || state === 'declined' || state === 'expired') {
    return EDIT_BOOKING_CODES.CANCELED;
  }
  if (
    state === 'delivered' ||
    state === 'reviewed' ||
    state === 'reviewed-by-customer' ||
    state === 'reviewed-by-provider'
  ) {
    return EDIT_BOOKING_CODES.NOT_UPCOMING;
  }
  const upcoming = isAcceptedUpcomingState(state, last) || isRequestedUpcomingState(state, last);
  if (!upcoming) {
    return EDIT_BOOKING_CODES.NOT_UPCOMING;
  }
  const nowDate = now instanceof Date ? now : new Date(now);
  if (bookingIsInSession(tx, nowDate)) {
    return EDIT_BOOKING_CODES.IN_SESSION;
  }
  const { start, end } = bookingTimes(tx);
  const nowMs = nowDate.getTime();
  if (end && nowMs >= end.getTime()) {
    return EDIT_BOOKING_CODES.NOT_UPCOMING;
  }
  // Same window as cancel: a missing start must not hide Edit. The order
  // breakdown can still show times from line items when booking is not
  // denormalized yet; tapping Edit used to no-op in that case.
  if (start && nowMs >= start.getTime()) {
    return EDIT_BOOKING_CODES.NOT_UPCOMING;
  }
  return null;
};

/**
 * True when start/end is a future 15-minute slot on a quarter-hour boundary.
 *
 * @param {Date|string|number} start
 * @param {Date|string|number} end
 * @param {Date|number} [now]
 * @returns {boolean}
 */
export const isEditableFifteenMinuteWindow = (start, end, now = new Date()) => {
  const parsed = parseFifteenMinuteWindow(start, end, now);
  return !!parsed;
};

/**
 * @param {Date|string|number} start
 * @param {Date|string|number} end
 * @param {Date|number} [now]
 * @returns {{ bookingStart: Date, bookingEnd: Date }|null}
 */
export const parseFifteenMinuteWindow = (start, end, now = new Date()) => {
  const bookingStart = asDate(start);
  const bookingEnd = asDate(end);
  if (!bookingStart || !bookingEnd) {
    return null;
  }
  const length = bookingEnd.getTime() - bookingStart.getTime();
  if (length !== SESSION_LENGTH_MS) {
    return null;
  }
  if (bookingStart.getTime() % SESSION_LENGTH_MS !== 0) {
    return null;
  }
  const nowMs = now instanceof Date ? now.getTime() : now;
  if (bookingStart.getTime() <= nowMs) {
    return null;
  }
  return { bookingStart, bookingEnd };
};

export const sameBookingWindow = (tx, start, end) => {
  const current = bookingTimes(tx);
  const nextStart = asDate(start);
  const nextEnd = asDate(end);
  if (!current.start || !nextStart || !nextEnd) {
    return false;
  }
  const currentEnd = current.end || new Date(current.start.getTime() + SESSION_LENGTH_MS);
  return current.start.getTime() === nextStart.getTime() && currentEnd.getTime() === nextEnd.getTime();
};

export const currentSessionFormat = tx => resolveSessionFormat(tx?.attributes?.protectedData || {});

export const holdAmountForFormat = format => priceInSubunitsForFormat(format);

/**
 * User-facing error for a failed edit. Never “just been booked” unless
 * another booking actually holds the seat.
 *
 * @param {string} [code]
 * @returns {string}
 */
export const editBookingErrorMessage = code => {
  switch (code) {
    case EDIT_BOOKING_CODES.NOT_UPCOMING:
    case EDIT_BOOKING_CODES.IN_SESSION:
      return 'This session has already started. Book a new time if you still want to talk.';
    case EDIT_BOOKING_CODES.SESSION_LEFT:
    case EDIT_BOOKING_CODES.CANCELED:
      return 'This booking has ended. Book a new time if you still want to talk.';
    case EDIT_BOOKING_CODES.FORMAT_INVALID:
    case EDIT_BOOKING_CODES.FORMAT_NOT_OFFERED:
      return 'This listener does not offer that session format.';
    case EDIT_BOOKING_CODES.WINDOW_INVALID:
      return 'Pick a later 15-minute time. Times in the next hour need Start now.';
    case EDIT_BOOKING_CODES.SEAT_UNAVAILABLE:
      return 'That time is not available. Pick another 15-minute window.';
    case EDIT_BOOKING_CODES.RATE_LIMITED:
      return 'Too many booking updates just now. Wait a minute and try again.';
    case EDIT_BOOKING_CODES.NOT_CUSTOMER:
      return 'Only the talker can edit this booking.';
    case EDIT_BOOKING_CODES.NO_PAYMENT_METHOD:
    case EDIT_BOOKING_CODES.HOLD_FAILED:
      return 'We could not update the card hold. Try again, or book a new time.';
    case EDIT_BOOKING_CODES.CANCEL_FAILED:
      return 'We could not replace this booking. Try again in a moment.';
    case 'transaction-booking-time-not-available':
      return 'That time is not available. Pick another 15-minute window.';
    default:
      return 'We could not update this booking. Try another time or format.';
  }
};

export const isSessionFormat = format => SESSION_FORMATS.includes(format);

/**
 * Transaction id for the booking/session page after a successful edit.
 * Prefers a replacement id from the API; otherwise keeps the current booking.
 *
 * @param {Object} [data]
 * @param {string} [currentId]
 * @returns {string|null}
 */
/**
 * Talker landed on the replacement order after Edit, or the new row
 * is still waiting for the listener to accept the new time.
 *
 * @param {Object} params
 * @param {boolean} [params.isCustomer]
 * @param {boolean} [params.editedQuery]
 * @param {boolean} [params.editedBooking]
 * @param {string} [params.processState]
 * @returns {boolean}
 */
export const isTalkerEditSuccessPage = ({
  isCustomer,
  editedQuery,
  editedBooking,
  processState,
} = {}) =>
  !!isCustomer &&
  (!!editedQuery || (!!editedBooking && processState === 'preauthorized'));

export const bookingPageIdAfterEdit = (data, currentId) => {
  return data?.transactionId || currentId || null;
};

export { SESSION_LENGTH_MS };
